/* THE INTAKE INBOX'S RULES — who is waiting, and what an answer does (W5, 2026-09-18).
 *
 * 🔴 **A REQUEST IS A DOORBELL, NOT A KEY — and this is the file that must never forget it.**
 * `CLAUDE.md` states it as a standing decision, and the phone's `decide()` says the same from the
 * other side: *"THIS GRANTS NOTHING — the grant on the table is `update (status, decided_at)` and
 * no policy anywhere reads this table, so the stamp is a record of an answer and cannot be the
 * premise of any permission."*
 *
 * ⚠️ **SO ACCEPTING DOES NOT MAKE SOMEBODY A CLIENT.** MEASURED from `pg_policy` 2026-09-18:
 * `coach_requests_coach_decide` is an UPDATE from `pending` to `accepted | declined` and nothing
 * else — no insert into `coach_clients`, no side effect anywhere. The coach still INVITES, and
 * only the invited person may activate. A screen that implied otherwise would be promising a
 * coach something the database will not do.
 */

export type RequestStatus = "pending" | "accepted" | "declined" | "withdrawn";

export type CoachRequest = {
  id: string;
  clientId: string | null;
  clientName: string | null;
  clientEmail: string | null;
  note: string | null;
  status: RequestStatus;
  askedAtISO: string;
};

const KNOWN: readonly string[] = ["pending", "accepted", "declined", "withdrawn"];

/** A status this app does not know is DROPPED, never rendered raw.
 *
 * ⚠️ The phone does the same (`compactMap` over `Status(rawValue:)`), and the reason is that a
 * status decides which ACTIONS a row offers — an unrecognised one would fall through to whatever
 * the last branch happens to be, which is how a request gets decided by a default. */
export function parseStatus(raw: string | null): RequestStatus | null {
  return raw !== null && KNOWN.includes(raw) ? (raw as RequestStatus) : null;
}

/** What a coach may do with this row, right now. */
export type RequestAction = "decide" | "none";

/**
 * ⚠️ **ONLY A PENDING REQUEST CAN BE ANSWERED, and the DATABASE is what enforces it** — the policy's
 * `USING` clause requires `status = 'pending'`, so a stale screen offering the buttons on a decided
 * row would have its write refused. This predicts that refusal rather than inventing a second rule:
 * the two agree because they are the same sentence, not because somebody kept them in step.
 */
export function actionFor(status: RequestStatus): RequestAction {
  return status === "pending" ? "decide" : "none";
}

/** How a decided request reads once it is decided. */
export function describeStatus(status: RequestStatus): string {
  switch (status) {
    case "pending": return "Waiting for you";
    case "accepted": return "Accepted";
    case "declined": return "Declined";
    // ⚠️ The CLIENT withdrew it. Said in their voice rather than as "cancelled", which would read
    // as something the coach or the app did.
    case "withdrawn": return "They withdrew it";
  }
}

/** Who is asking, when the name is missing. */
export function describeAsker(r: CoachRequest): string {
  const name = (r.clientName ?? "").trim();
  if (name !== "") return name;
  const email = (r.clientEmail ?? "").trim();
  if (email !== "") return email;
  // ⚠️ NEVER a blank. A row with neither is still a real person waiting, and an empty line reads
  // as a broken screen rather than as a missing name.
  return "Someone without a name yet";
}

/** Pending first, then newest first — a queue reads as a queue. */
export function sortForInbox(rs: CoachRequest[]): CoachRequest[] {
  return [...rs].sort((a, b) => {
    const ap = a.status === "pending" ? 0 : 1;
    const bp = b.status === "pending" ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return b.askedAtISO.localeCompare(a.askedAtISO);
  });
}

/** How many still need an answer — the number the section heading carries. */
/** JOB 3 STEP 5 PAGE 6 — the page's empty line, TRUE in both states of the Community switch. A request is written only by
 *  the phone's Coaches directory (`CoachDiscoveryService.ask`); with the Community out nothing writes one, and clients join
 *  by the coach's invite link — so the line says that, never "publish your coaching page" (a page that is not drawn). */
export function requestsEmptyLine(included: boolean): string {
  return included
    ? "No requests yet. People find you through your coaching page — publish it and share the link."
    : "No requests. Clients join by your invite link.";
}

export function pendingCount(rs: CoachRequest[]): number {
  return rs.filter((r) => r.status === "pending").length;
}
